"use client";

import { Button } from "@/components/ui/button";
import type { SafeHtml } from "@/lib/safeHtml";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

type ExpandableTextProps = {
  initialChars?: number;
  className?: string;
  moreLabel?: string;
  lessLabel?: string;
  /**
   * Applied to the html container while collapsed, e.g.
   * "line-clamp-5 sm:line-clamp-none". Callers pass the class so Tailwind can
   * see it statically and so the breakpoint stays where the layout is decided.
   */
  collapsedClassName?: string;
  /** Applied to the toggle wrapper, e.g. "sm:hidden". */
  buttonClassName?: string;
} & (
  | { text: string; isHtml?: false }
  // Callers own sanitization in html mode; SafeHtml is the proof.
  | { text: SafeHtml; isHtml: true }
);

export default function ExpandableText({
  text,
  initialChars = 220,
  className,
  moreLabel = "Show more",
  lessLabel = "Show less",
  collapsedClassName,
  buttonClassName,
  isHtml = false,
}: ExpandableTextProps) {
  const [expanded, setExpanded] = useState(false);
  const plainText = isHtml ? text.replace(/<[^>]*>/g, "") : text;

  const shouldTruncate = plainText.length > initialChars;
  // Html keeps its markup and is clamped visually; plain text is sliced, which
  // markup cannot survive.
  const shownText =
    isHtml || !shouldTruncate || expanded
      ? text
      : `${plainText.slice(0, initialChars)}…`;

  return (
    <div className={className}>
      {isHtml ? (
        <div
          className={cn(
            "redactor-rich-text-html text-foreground",
            shouldTruncate && !expanded && collapsedClassName,
          )}
          dangerouslySetInnerHTML={{ __html: shownText }}
        />
      ) : (
        <div className="text-foreground whitespace-pre-line">{shownText}</div>
      )}
      {shouldTruncate && (
        <div className={cn("mt-2", buttonClassName)}>
          <Button
            variant="secondary"
            className="px-6 py-2 rounded-full text-secondary-foreground h-12"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? (
              <span className="inline-flex items-center gap-1">
                {lessLabel}
                <ChevronUp className="h-4 w-4" />
              </span>
            ) : (
              <span className="inline-flex items-center gap-1">
                {moreLabel}
                <ChevronDown className="h-4 w-4" />
              </span>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
